import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isNonWorkingDate } from "../../utils/availabilitySchedule";
import "./CalendarGrid.css";

// Generate 15-minute slots from 08:00 to 20:00
const SLOTS_15MIN = (() => {
  const slots = [];
  for (let hour = 8; hour < 20; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const slotId = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
      slots.push({ h: hour, m: minute, slotId });
    }
  }
  return slots;
})();

// Keep old constants for compatibility with week view
const HOURS = Array.from({ length: 12 }, (_, i) => {
  const h = i + 8;
  const label = `${String(h).padStart(2, "0")}:00`;
  return { h, label };
});

const STATUS_STYLE = {
  confirmed: { color: "#006eea", border: "#006eea" },
  pending:   { color: "#d97706", border: "#d97706" },
  waiting:   { color: "#0284c7", border: "#0284c7" },
  in_consultation: { color: "#006eea", border: "#006eea" },
  "in-progress": { color: "#006eea", border: "#006eea" },
  in_progress: { color: "#006eea", border: "#006eea" },
  completed: { color: "#166534", border: "#166534" },
  done: { color: "#166534", border: "#166534" },
  cancelled: { color: "#ef4444", border: "#ef4444" },
  canceled: { color: "#ef4444", border: "#ef4444" },
  rescheduled: { color: "#0c4a6e", border: "#0c4a6e" },
};

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function rangesOverlap(startA, endA, startB, endB) {
  return startA < endB && endA > startB;
}

function isBlockedSlot(blockedSlots, start, end) {
  return blockedSlots.some((slot) => {
    const blockStart = new Date(slot?.startTime).getTime();
    const blockEnd = new Date(slot?.endTime).getTime();
    return Number.isFinite(blockStart) && Number.isFinite(blockEnd) &&
      rangesOverlap(start, end, blockStart, blockEnd);
  });
}

function isDateBeforeToday(date, today) {
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return dateOnly < todayOnly;
}

function formatTimeSlot(startH, startM, endH, endM) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(startH)}:${pad(startM)} - ${pad(endH)}:${pad(endM)}`;
}

function parseDateStr(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getMonthGrid(anchorStr) {
  const anchor = parseDateStr(anchorStr);
  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const firstDay = new Date(year, month, 1);
  const dow = firstDay.getDay(); // 0=Sun
  const startOffset = dow === 0 ? -6 : 1 - dow;
  const cur = new Date(year, month, 1 + startOffset);
  const weeks = [];
  for (let w = 0; w < 6; w++) {
    const week = Array.from({ length: 7 }, () => {
      const d = new Date(cur);
      cur.setDate(cur.getDate() + 1);
      return d;
    });
    weeks.push(week);
    if (cur.getMonth() > month || cur.getFullYear() > year) break;
  }
  return weeks;
}

export default function CalendarGrid({
  weekDates,
  viewMode,
  today,
  anchorDate,
  appointments = [],
  blockedSlots = [],
  workingPeriods = [],
  onBack,
  onForward,
  onChangeViewMode,
  onNewAppointment,
  onConfirmAppointment,
  onOpenAppointment,
  loading = false,
}) {
  const { t } = useTranslation("cabinetDashboard");
  const locale = t("dashboard.locale", { defaultValue: "en-US" });
  const getStatusLabel = (status) =>
    t(`dashboard.calendar.status.${status}`, { defaultValue: status });
  const VIEW_MODES = [
    { key: "Day",   label: t("dashboard.calendar.day") },
    { key: "Week",  label: t("dashboard.calendar.week") },
    { key: "Month", label: t("dashboard.calendar.month") },
  ];
  const shortDayNames = Array.from({ length: 7 }, (_, index) =>
    new Date(2026, 0, 5 + index).toLocaleDateString(locale, { weekday: "short" })
  );

  const dayDate = anchorDate ? parseDateStr(anchorDate) : today;
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const daySlotsRef = useRef(null);
  const weekTableRef = useRef(null);
  const autoScrolledRef = useRef("");

  useEffect(() => {
    const interval = window.setInterval(() => setCurrentTime(new Date()), 60_000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const scrollKey = `${viewMode}:${anchorDate}`;
    if (autoScrolledRef.current === scrollKey || !isSameDay(dayDate, currentTime)) return;

    if (viewMode === "Day" && daySlotsRef.current) {
      const currentSlot = daySlotsRef.current.querySelector(".cg__day-slot--now");
      if (currentSlot) {
        daySlotsRef.current.scrollTop =
          currentSlot.offsetTop - daySlotsRef.current.offsetTop - daySlotsRef.current.clientHeight / 3;
        autoScrolledRef.current = scrollKey;
      }
    } else if (viewMode === "Week" && weekTableRef.current) {
      const currentRow = weekTableRef.current.querySelector(`[data-hour="${currentTime.getHours()}"]`);
      if (currentRow) {
        weekTableRef.current.scrollTop =
          currentRow.offsetTop - weekTableRef.current.offsetTop - weekTableRef.current.clientHeight / 3;
        autoScrolledRef.current = scrollKey;
      }
    }
  }, [viewMode, anchorDate, dayDate, currentTime]);

  const dayTitle = dayDate.toLocaleDateString(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const getSourceLabel = (appt) => {
    if (appt?.source === "event") return t("dashboard.calendar.event", { defaultValue: "Event" });
    if (appt?.patientSource === "local") return t("dashboard.calendar.localPatient", { defaultValue: "Local patient" });
    if (appt?.patientSource === "subprofile") return "Subprofile";
    return t("dashboard.calendar.medilink", { defaultValue: "Medilink" });
  };

  const renderPatientLabel = (appt) => {
    const parts = [appt?.time, appt?.patient].filter(Boolean);
    return parts.join(" ");
  };

  return (
    <div className="cg__wrapper" aria-busy={loading}>
      {loading && (
        <div className="cg__skeleton" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      )}
      {/* toolbar */}
      <div className="cg__toolbar">
        {viewMode !== "Day" && (
          <div className="cg__nav" aria-label={t("dashboard.calendar.dateNavigation", { defaultValue: "Date navigation" })}>
            <button className="cg__nav-btn" onClick={onBack} aria-label={t("dashboard.calendar.previousDate", { defaultValue: "Previous date" })}>
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </button>

            <button className="cg__nav-btn" onClick={onForward} aria-label={t("dashboard.calendar.nextDate", { defaultValue: "Next date" })}>
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path d="M9 18l6-6-6-6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        )}

        <div className="cg__toolbar-right">
          <div className="cg__view-toggle">
            {VIEW_MODES.map((m) => (
              <button
                key={m.key}
                className={`cg__view-btn ${viewMode === m.key ? "cg__view-btn--active" : ""}`}
                onClick={() => onChangeViewMode(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Day view */}
      {viewMode === "Day" ? (
        <section className="cg">
          <div className="cg__day-view">
            <div className="cg__day-view-header">
              <div className="cg__day-view-heading">
                <h2 className="cg__day-view-title">{dayTitle}</h2>
                <div className="cg__date-nav" aria-label={t("dashboard.calendar.dateNavigation", { defaultValue: "Date navigation" })}>
                  <button className="cg__nav-btn" onClick={onBack} aria-label={t("dashboard.calendar.previousDate", { defaultValue: "Previous date" })}>
                    <svg viewBox="0 0 24 24" width="14" height="14">
                      <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  </button>
                  <button className="cg__nav-btn" onClick={onForward} aria-label={t("dashboard.calendar.nextDate", { defaultValue: "Next date" })}>
                    <svg viewBox="0 0 24 24" width="14" height="14">
                      <path d="M9 18l6-6-6-6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
              </div>
              <span className="cg__day-view-sub">
                {(() => { const n = appointments.filter(a => a.date === anchorDate).length; return `${n} ${n !== 1 ? t("dashboard.calendar.scheduledPlural") : t("dashboard.calendar.scheduled")}`; })()}
              </span>
            </div>
            <div className="cg__day-slots" ref={daySlotsRef}>
              {SLOTS_15MIN.map(({ h, m, slotId }) => {
                const endM = m + 15;
                const endH = endM >= 60 ? h + 1 : h;
                const finalEndM = endM >= 60 ? endM - 60 : endM;
                const slotLabel = formatTimeSlot(h, m, endH, finalEndM);
                
                const appt = appointments.find(
                  (a) => {
                    // Check if appointment is on this date
                    if (a.date !== anchorDate) return false;
                    
                    // Check if appointment hour matches
                    if (a.hour !== h) return false;
                    
                    // If appointment has minute defined, match exact minute
                    // Otherwise, match any minute in this hour (backward compatibility)
                    if (typeof a.minute === "number") {
                      return a.minute === m;
                    } else {
                      return true; // Old format: entire hour is occupied
                    }
                  }
                );
                const st = appt ? (STATUS_STYLE[appt.status] || STATUS_STYLE.confirmed) : null;
                const dayDate = parseDateStr(anchorDate);
                const isPastDate = isDateBeforeToday(dayDate, today);
                const isNonWorkingDay = isNonWorkingDate(dayDate, workingPeriods);
                
                // Check if this specific time slot is in the past
                const slotDateTime = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), h, m);
                const slotStart = slotDateTime.getTime();
                const slotEnd = slotStart + 15 * 60 * 1000;
                const isBlocked = isBlockedSlot(blockedSlots, slotStart, slotEnd);
                const now = currentTime;
                const isPastSlot = slotDateTime < now;
                const isDisabled = isPastDate || isPastSlot || isBlocked || isNonWorkingDay;
                const isNowSlot =
                  isSameDay(dayDate, currentTime) &&
                  h === currentTime.getHours() &&
                  m === Math.floor(currentTime.getMinutes() / 15) * 15;
                const status = appt?.status === "in_consultation" || appt?.status === "in_progress"
                  ? "in-progress"
                  : appt?.status;
                
                return (
                  <div
                    key={slotId}
                    data-slot={slotId}
                    className={`cg__day-slot${isNowSlot ? " cg__day-slot--now" : ""}`}
                    style={isNowSlot ? { "--now-offset": `${(currentTime.getMinutes() % 15) / 15 * 100}%` } : undefined}
                  >
                    <span className="cg__day-slot-time">{slotLabel}</span>
                    {isNowSlot && <span className="cg__now-label">{t("dashboard.calendar.now")}</span>}
                    {appt ? (
                      <div
                        className={`cg__appt-card cg__appt-card--${status || "confirmed"}`}
                        title={`${appt.patient || t("dashboard.widgets.patient")}${appt.notes ? ` · ${appt.notes}` : ""}`}
                      >
                        <button
                          type="button"
                          className="cg__appt-body"
                          onClick={() => onOpenAppointment?.(appt)}
                          aria-label={`${appt.time} ${appt.patient || t("dashboard.widgets.patient")}, ${getStatusLabel(status)}`}
                        >
                          <span className="cg__appt-name">
                            {appt.time}&nbsp;<span className="cg__appt-dot">·</span>&nbsp;{appt.patient}
                            {appt.patientAge && <span> ({appt.patientAge}y)</span>}
                          </span>
                          {appt.patientIdentifier && (
                            <span className="cg__appt-identifier" style={{ fontSize: 11, color: "#8b5cf6" }}>
                              {appt.patientIdentifier}
                            </span>
                          )}
                          {appt.notes && <span className="cg__appt-notes">{appt.notes}</span>}
                          <div className="cg__appt-source">
                            <small style={{ color: "#546881", fontSize: 11 }}>
                              {appt.patientSource === "local" ? "📍 Local" : appt.source === "event" ? "📅 Event" : "🔗 Medilink"}
                            </small>
                          </div>
                        </button>
                        <div className="cg__appt-actions">
                          {appt.status === "pending" && appt.source === "rdv" && (
                            <button
                              className="cg__confirm-btn"
                              onClick={(event) => {
                                event.stopPropagation();
                                onConfirmAppointment?.(appt);
                              }}
                              title={t("dashboard.calendar.confirm")}
                            >
                              {t("dashboard.calendar.confirm")}
                            </button>
                          )}
                          <span
                            className="cg__appt-badge"
                            style={{ color: st.color, borderColor: st.border }}
                          >
                            {getStatusLabel(status)}
                          </span>
                        </div>
                      </div>
                    ) : isBlocked ? (
                      <div className="cg__blocked-slot" title={t("dashboard.calendar.blocked", { defaultValue: "Blocked" })}>
                        <span className="cg__appt-badge cg__blocked-badge">{t("dashboard.calendar.blocked", { defaultValue: "Blocked" })}</span>
                      </div>
                    ) : (
                      <button
                        className={`cg__day-add-btn${isDisabled ? " cg__day-add-btn--disabled" : ""}`}
                        onClick={() => !isDisabled && onNewAppointment?.('add', dayDate, h, m)}
                        disabled={isDisabled}
                        title={isNonWorkingDay ? t("dashboard.calendar.nonWorkingDay", { defaultValue: "Non-working day" }) : isPastSlot ? t("dashboard.calendar.pastSlot", { defaultValue: "Ce créneau est déjà passé." }) : isDisabled ? t("dashboard.calendar.pastDate", { defaultValue: "Cannot add slots to past dates" }) : ""}
                      >
                        {t("dashboard.calendar.addSlot")}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

      ) : viewMode === "Month" ? (
        /* Month view */
        <section className="cg">
          <div className="cg__month-grid">
            <div className="cg__month-header">
              {shortDayNames.map((d) => (
                <div key={d} className="cg__month-day-name">{d}</div>
              ))}
            </div>
            {getMonthGrid(anchorDate).map((week, wi) => (
              <div key={wi} className="cg__month-week">
                {week.map((d, di) => {
                  const anchor = parseDateStr(anchorDate);
                  const isCurrentMonth = d.getMonth() === anchor.getMonth();
                  const isToday = isSameDay(d, today);
                  const dateStr = toDateStr(d);
                  const dayItems = appointments.filter((a) => a.date === dateStr);
                  const appointmentItems = dayItems.filter((a) => a.source !== "event");
                  const eventItems = dayItems.filter((a) => a.source === "event");
                  const totalCount = dayItems.length;
                  const isNonWorkingDay = isNonWorkingDate(d, workingPeriods);
                  return (
                    <div
                      key={di}
                      className={`cg__month-cell${!isCurrentMonth ? " cg__month-cell--out" : ""}${isNonWorkingDay ? " cg__month-cell--nonworking" : ""}`}
                    >
                      <span className={`cg__month-cell-num${isToday ? " cg__month-cell-num--today" : ""}`}>
                        {d.getDate()}
                      </span>

                      {totalCount > 0 && (
                        <div className="cg__month-cell-summary" aria-label={`${totalCount} items`}> 
                          {appointmentItems.length > 0 && (
                            <span className="cg__month-count cg__month-count--appt">
                              <span className="cg__month-count-value">{appointmentItems.length}</span>
                              <span className="cg__month-count-label">{t("dashboard.calendar.appointments", { defaultValue: "Appointments" })}</span>
                            </span>
                          )}
                          {eventItems.length > 0 && (
                            <span className="cg__month-count cg__month-count--event">
                              <span className="cg__month-count-value">{eventItems.length}</span>
                              <span className="cg__month-count-label">{t("dashboard.calendar.events", { defaultValue: "Events" })}</span>
                            </span>
                          )}
                        </div>
                      )}

                      {totalCount > 0 && (
                        <div className="cg__month-popover" role="tooltip">
                          {appointmentItems.length > 0 && (
                            <div className="cg__month-popover-section">
                              <div className="cg__month-popover-title">
                                {t("dashboard.calendar.appointments", { defaultValue: "Appointments" })}
                              </div>
                              <ul className="cg__month-popover-list">
                                {appointmentItems.map((appt) => (
                                  <li key={appt.id} className={`cg__month-popover-item cg__month-popover-item--${appt.status}`}>
                                    <span className="cg__month-popover-time">{appt.time}</span>
                                    <span className="cg__month-popover-name">
                                      {renderPatientLabel(appt)}{appt.patientAge ? ` (${appt.patientAge}y)` : ""}
                                    </span>
                                    <span className="cg__month-popover-source">{getSourceLabel(appt)}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {eventItems.length > 0 && (
                            <div className="cg__month-popover-section">
                              <div className="cg__month-popover-title">
                                {t("dashboard.calendar.events", { defaultValue: "Events" })}
                              </div>
                              <ul className="cg__month-popover-list">
                                {eventItems.map((appt) => (
                                  <li key={appt.id} className="cg__month-popover-item cg__month-popover-item--event">
                                    <span className="cg__month-popover-time">{appt.time}</span>
                                    <span className="cg__month-popover-name">{renderPatientLabel(appt)}</span>
                                    <span className="cg__month-popover-source">{getSourceLabel(appt)}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </section>

      ) : (
        /* Week agenda table */
        <section className="cg">
          <div className="cg__week-table-wrap" ref={weekTableRef}>
            <table className="cg__week-table">
              <thead>
                <tr>
                  <th className="cg__week-table-time-head" scope="col" />
                  {weekDates.map((d, i) => {
                    const isToday = isSameDay(d, today);
                    return (
                      <th
                        key={i}
                        className={`cg__week-table-day-head ${isToday ? "cg__week-table-day-head--today" : ""}`}
                        scope="col"
                      >
                        <span className="cg__week-table-day-name">
                          {d.toLocaleDateString(locale, { weekday: "short" })}
                        </span>
                        <span className={`cg__week-table-day-num ${isToday ? "cg__week-table-day-num--today" : ""}`}>
                          {d.getDate()}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {HOURS.map(({ h, label }) => (
                  <tr key={h} data-hour={h} className="cg__week-table-row">
                    <th className="cg__week-table-time" scope="row">
                      {label}
                    </th>

                    {weekDates.map((d, ci) => {
                      const pad = (n) => String(n).padStart(2, "0");
                      const dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
                      const cellAppointments = appointments.filter(
                        (a) => a.hour === h && a.date === dateStr
                      );
                      const isPastDate = isDateBeforeToday(d, today);
                      const isNonWorkingDay = isNonWorkingDate(d, workingPeriods);
                      const hourStart = new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, 0).getTime();
                      const isBlocked = isBlockedSlot(blockedSlots, hourStart, hourStart + 60 * 60 * 1000);
                      const isPastTime = isSameDay(d, currentTime) && hourStart < currentTime.getTime();
                      const isNowHour = isSameDay(d, currentTime) && h === currentTime.getHours();

                      return (
                        <td
                          key={ci}
                          className={`cg__week-table-cell${cellAppointments.length ? "" : " cg__week-table-cell--empty"}${isPastDate || isPastTime ? " cg__week-table-cell--past" : ""}${isBlocked ? " cg__week-table-cell--blocked" : ""}${isNonWorkingDay ? " cg__week-table-cell--nonworking" : ""}${isNowHour ? " cg__week-table-cell--now" : ""}`}
                          style={isNowHour ? { "--now-offset": `${currentTime.getMinutes() / 60 * 100}%` } : undefined}
                          title={isPastDate || isPastTime ? t("dashboard.calendar.pastSlot", { defaultValue: "Ce créneau est déjà passé." }) : undefined}
                          onClick={() => cellAppointments.length === 0 && !isPastDate && !isPastTime && !isBlocked && !isNonWorkingDay && onNewAppointment?.('add', new Date(dateStr), h)}
                        >
                          <div className="cg__week-table-cell-inner">
                            {isNowHour && <span className="cg__now-label">{t("dashboard.calendar.now")}</span>}
                            {cellAppointments.length > 0 ? (
                              cellAppointments.map((appt) => {
                                const st = STATUS_STYLE[appt.status] || STATUS_STYLE.confirmed;
                                return (
                                  <div
                                    key={appt.id}
                                    className={`cg__week-card cg__week-card--${appt.status}`}
                                  >
                                    <button
                                      type="button"
                                      className="cg__week-card-open"
                                      onClick={() => onOpenAppointment?.(appt)}
                                      aria-label={`${appt.time} ${appt.patient || t("dashboard.widgets.patient")}, ${getStatusLabel(appt.status)}`}
                                    >
                                      <span className="cg__week-card-name">
                                        {appt.time} {appt.patient}
                                        {appt.patientAge && <span> ({appt.patientAge}y)</span>}
                                      </span>
                                      {appt.patientIdentifier && (
                                        <span className="cg__week-card-id" style={{ fontSize: 10, color: "#8b5cf6" }}>
                                          {appt.patientIdentifier}
                                        </span>
                                      )}
                                      <span className="cg__week-card-time">{appt.time}</span>
                                      <span className="cg__week-card-source">
                                        {appt.patientSource === "local" ? "📍 Local" : appt.source === "event" ? "📅 Event" : "🔗 Medilink"}
                                      </span>
                                    </button>
                                    {appt.status === "pending" && appt.source === "rdv" && (
                                      <button
                                        className="cg__week-confirm-btn"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          onConfirmAppointment?.(appt);
                                        }}
                                      >
                                        {t("dashboard.calendar.confirm")}
                                      </button>
                                    )}
                                    <span
                                      className="cg__appt-badge"
                                      style={{ color: st.color, borderColor: st.border, alignSelf: "flex-start" }}
                                    >
                                      {getStatusLabel(appt.status)}
                                    </span>
                                  </div>
                                );
                              })
                            ) : isBlocked ? (
                              <div className="cg__blocked-slot cg__blocked-slot--week">
                                <span className="cg__appt-badge cg__blocked-badge">{t("dashboard.calendar.blocked", { defaultValue: "Blocked" })}</span>
                              </div>
                            ) : (
                              <button
                                className={`cg__week-empty-btn${isPastDate || isPastTime || isNonWorkingDay ? " cg__week-empty-btn--disabled" : ""}`}
                                onClick={() => !isPastDate && !isPastTime && !isNonWorkingDay && onNewAppointment?.('add', new Date(dateStr), h)}
                                disabled={isPastDate || isPastTime || isNonWorkingDay || isBlocked}
                                title={isNonWorkingDay ? t("dashboard.calendar.nonWorkingDay", { defaultValue: "Non-working day" }) : isPastDate || isPastTime ? t("dashboard.calendar.pastSlot", { defaultValue: "Ce créneau est déjà passé." }) : ""}
                              >
                                {t("dashboard.calendar.addSlot")}
                              </button>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
